import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  TextField,
  IconButton,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  useTheme
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import {
  setSearchQuery,
  selectProduct,
  clearSearch
} from "../../../store/slices/productsSlice";
import { useGlobalSearch } from "../../../hooks/useGlobalSearch";

/**
 * Componente de barra de pesquisa com autocomplete inteligente.
 * Busca em tempo real por nome, marca, categoria e descrição dos produtos.
 * Exibe sugestões em dropdown com limite de 10 itens.
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {boolean} [props.fullWidth=false] - Se deve ocupar toda a largura disponível
 * @returns {JSX.Element} Barra de pesquisa com autocomplete e ícones
 */
function SearchBar({ fullWidth = false }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { searchQuery, filteredProducts } = useSelector(
    (state) => state.products
  );
  const { searchAllProducts } = useGlobalSearch();
  const [showResults, setShowResults] = useState(false);
  
  // Usa pesquisa global para garantir resultados de TODOS os produtos
  const globalSearchResults = searchQuery ? searchAllProducts(searchQuery) : [];

  const handleChange = (e) => {
    dispatch(setSearchQuery(e.target.value));
    setShowResults(e.target.value.length > 0);
  };

  const handleClear = () => {
    dispatch(clearSearch());
    setShowResults(false);
  };

  const handleProductSelect = (product) => {
    dispatch(selectProduct(product));
    setShowResults(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setShowResults(false)}>
      <Box sx={{ position: "relative", width: fullWidth ? "100%" : "100%" }}>
        <TextField
          placeholder="O que você está procurando?"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleChange}
          onFocus={() => searchQuery.length > 0 && setShowResults(true)}
          InputProps={{
            endAdornment: (
              <IconButton
                size="small"
                onClick={searchQuery ? handleClear : undefined}
                sx={{ p: 0.5 }}
              >
                {searchQuery ? <ClearOutlinedIcon /> : <SearchOutlinedIcon />}
              </IconButton>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: theme.custom?.searchBar?.backgroundColor || "#FFFFFF",
              "& fieldset": {
                borderColor: theme.custom?.searchBar?.borderColor || "#E0E0E0"
              },
              "&:hover fieldset": {
                borderColor: "secondary.main"
              },
              "&.Mui-focused fieldset": {
                borderColor: "secondary.main"
              }
            }
          }}
        />
        {showResults && globalSearchResults.length > 0 && (
          <Paper
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 300,
              overflow: "auto"
            }}
          >
            <List>
              {/* Usa resultados da pesquisa global que considera TODOS os produtos */}
              {globalSearchResults.slice(0, 10).map((product) => (
                <ListItem
                  key={product.id}
                  component="button"
                  onClick={() => handleProductSelect(product)}
                  sx={{ cursor: "pointer" }}
                >
                  <ListItemText
                    primary={product.nome}
                    secondary={`${product.marca} - ${product.categoria}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

export default SearchBar;
